import { __esDecorate, __runInitializers } from "tslib";
import "core/Engines/Extensions/engine.renderTarget.js";
import { UndecorateSymbol } from "../utils/shaderCodeUtils.js";
import { ShaderRuntime } from "../runtime/shaderRuntime.js";
import { ConnectionPointType } from "../connection/connectionPointType.js";
import { CreateCommand } from "../command/command.js";
import { RegisterFinalRenderCommand } from "../utils/renderTargetUtils.js";
import { BaseBlock } from "./baseBlock.js";
import { TextureFormat, TextureType } from "./textureOptions.js";
import { EditableInPropertyPage, PropertyTypeForEdition } from "../editorUtils/editableInPropertyPage.js";
const OutputTexturePropertiesGroupName = "OUTPUT TEXTURE PROPERTIES";
/**
 * This is the base class for all shader blocks.
 *
 * It contains the redundant part of wrapping a shader for a full screen pass.
 *
 * The only required function to implement is the bind function.
 */
let ShaderBlock = (() => {
    var _a;
    let _classSuper = BaseBlock;
    let _outputTextureOptions_decorators;
    let _outputTextureOptions_initializers = [];
    let _outputTextureOptions_extraInitializers = [];
    return _a = class ShaderBlock extends _classSuper {
            /**
             * Disconnects the block from the graph.
             * @param disconnectedConnections - Stores the connections that have been broken in the process. You can reconnect them later if needed.
             */
            disconnectFromGraph(disconnectedConnections) {
                const input = this._getConnectionForMainInputTexture();
                for (const endpoint of this.output.endpoints) {
                    disconnectedConnections?.push([endpoint, this.output]);
                    input.connectTo(endpoint);
                }
            }
            _getConnectionForMainInputTexture() {
                const mainInputTextureName = this.getShaderProgram().fragment.mainInputTexture;
                if (!mainInputTextureName) {
                    throw `The block named "${this.name}" does not have a main input texture defined!`;
                }
                const mainInputTexture = this.findInput(UndecorateSymbol(mainInputTextureName));
                if (!mainInputTexture || !mainInputTexture.connectedTo) {
                    throw `The main input texture "${mainInputTextureName}" of block named "${this.name}" is not connected!`;
                }
                return mainInputTexture.connectedTo;
            }
            /**
             * Instantiates a new block.
             * @param smartFilter - Defines the smart filter the block belongs to
             * @param name - Defines the name of the block
             * @param disableOptimization - Defines if the block should not be optimized (default: false)
             */
            constructor(smartFilter, name, disableOptimization = false) {
                super(smartFilter, name, disableOptimization);
                /**
                 * The output connection point of the block.
                 */
                this.output = this._registerOutput("output", ConnectionPointType.Texture);
                /**
                 * The options used when creating the texture this block outputs to
                 */
                this.outputTextureOptions = __runInitializers(this, _outputTextureOptions_initializers, {
                    ratio: 1,
                    format: TextureFormat.RGBA,
                    type: TextureType.UNSIGNED_BYTE,
                });
                __runInitializers(this, _outputTextureOptions_extraInitializers);
            }
            /**
             * Gets the shader program to use to render the block.
             * @returns The shader program to use to render the block
             */
            getShaderProgram() {
                return this.constructor.ShaderCode;
            }
            /**
             * Generates the commands needed to execute the block at runtime and gathers promises for initialization work
             * @param initializationData - The initialization data to use
             * @param finalOutput - Defines if the block is the final output of the smart filter
             */
            generateCommandsAndGatherInitPromises(initializationData, finalOutput) {
                const runtime = initializationData.runtime;
                const shaderBlockRuntime = new ShaderRuntime(runtime.effectRenderer, this.getShaderProgram(), this.getShaderBinding());
                initializationData.initializationPromises.push(shaderBlockRuntime.onReadyAsync);
                runtime.registerResource(shaderBlockRuntime);
                if (finalOutput) {
                    RegisterFinalRenderCommand(initializationData.outputBlock, runtime, this, shaderBlockRuntime);
                }
                else {
                    const renderTargetTexture = this.output.runtimeData?.value;
                    if (!renderTargetTexture) {
                        throw new Error(`${this.blockType} did not have a render target texture.`);
                    }
                    runtime.registerCommand(CreateCommand(`${this.blockType}.render`, this, () => {
                        shaderBlockRuntime.renderToTargetTexture(renderTargetTexture);
                    }));
                }
                super.generateCommandsAndGatherInitPromises(initializationData, finalOutput);
            }
        },
        (() => {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(_classSuper[Symbol.metadata] ?? null) : void 0;
            _outputTextureOptions_decorators = [EditableInPropertyPage("Ratio", PropertyTypeForEdition.Float, OutputTexturePropertiesGroupName, {
                    min: 0.1,
                    max: 10.0,
                    notifiers: { rebuild: true },
                    subPropertyName: "ratio",
                }), EditableInPropertyPage("Format", PropertyTypeForEdition.List, OutputTexturePropertiesGroupName, {
                    notifiers: { rebuild: true },
                    subPropertyName: "format",
                    options: [
                        { label: "R", value: TextureFormat.R },
                        { label: "RG", value: TextureFormat.RG },
                        { label: "RGBA", value: TextureFormat.RGBA },
                    ],
                }), EditableInPropertyPage("Type", PropertyTypeForEdition.List, OutputTexturePropertiesGroupName, {
                    notifiers: { rebuild: true },
                    subPropertyName: "type",
                    options: [
                        { label: "UByte", value: TextureType.UNSIGNED_BYTE },
                        { label: "Float", value: TextureType.FLOAT },
                        { label: "Half Float", value: TextureType.HALF_FLOAT },
                    ],
                })];
            __esDecorate(null, null, _outputTextureOptions_decorators, { kind: "field", name: "outputTextureOptions", static: false, private: false, access: { has: obj => "outputTextureOptions" in obj, get: obj => obj.outputTextureOptions, set: (obj, value) => { obj.outputTextureOptions = value; } }, metadata: _metadata }, _outputTextureOptions_initializers, _outputTextureOptions_extraInitializers);
            if (_metadata) Object.defineProperty(_a, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        })(),
        /**
         * The class name of the block.
         */
        _a.ClassName = "ShaderBlock",
        _a;
})();
export { ShaderBlock };
//# sourceMappingURL=shaderBlock.js.map